// client/src/services/authClient.ts (TypeScript since ST-00216; behaviour unchanged)
//
// Shared auth plumbing for ALL axios clients (api.ts, apiService.js,
// weighbridgeComplianceApi.ts). The single-flight refresh here is the fix for
// the #1 risk (Hardening §1): if several requests 401 at once, only ONE
// /auth/refresh goes out and everyone awaits it — preventing the
// false-positive reuse-detection that would otherwise revoke the family and
// bounce the user to /login.
//
// Also: withCredentials (cookies), double-submit CSRF header on mutations, and
// a single 401 -> refresh -> retry-once policy.
//
// ST-00303: a refresh that never reached LexiBridge (no network, a company web
// filter, a deploy's 502) is not a lost session. isServerUnreachable() tells the
// two apart, and only a refresh the server itself refused signs the user out.

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestHeaders,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import type {
  AccessBlockedCode,
  AccessBlockedHandler,
  AuthLostHandler,
  HttpErrorLike,
  HttpResponseLike,
  SessionRefreshedListener,
} from '../types/clientShell';

export type { AccessBlockedCode, AccessBlockedHandler, AuthLostHandler };

function apiBase(): string {
  let url: string = import.meta.env.REACT_APP_API_URL || 'http://localhost:5002/api';
  url = url.replace(/\/+$/, '');
  if (!/\/api$/.test(url)) url += '/api';
  return url;
}

// ---- "can't reach LexiBridge" (ST-00303) -----------------------------------
// The answers a gateway gives when the API behind it is down or restarting.
const GATEWAY_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

/** The parts of any HTTP answer (a successful one included) the checks below read. */
type HttpAnswer = Pick<HttpResponseLike, 'status' | 'headers'>;

/** A response header, whether axios hands over AxiosHeaders or a plain object. */
function headerOf(res: HttpAnswer, name: string): string {
  const headers = res.headers;
  if (!headers || typeof headers !== 'object') return '';
  const getter = (headers as { get?: unknown }).get;
  if (typeof getter === 'function') {
    const value: unknown = getter.call(headers, name);
    return typeof value === 'string' ? value : '';
  }
  const wanted = name.toLowerCase();
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === wanted && typeof value === 'string') return value;
  }
  return '';
}

/**
 * True when a response did not come from LexiBridge's API: the API answers JSON
 * (routes and middleware/errorResponses alike), so an HTML page in its place is a
 * web filter's block or sign-in page, a captive portal, or a proxy error page.
 */
export function isForeignResponse(res: HttpAnswer | null | undefined): boolean {
  if (!res) return false;
  return /\btext\/html\b/i.test(headerOf(res, 'content-type'));
}

/**
 * True when a request failed because LexiBridge could not be reached, rather
 * than because the server refused it:
 *  - no answer at all (network down, timeout, a web filter redirecting the call
 *    to its own sign-in page, which the browser then blocks), but not a request
 *    the app cancelled itself;
 *  - 502 / 503 / 504 from the proxy (the API is down or restarting, e.g. a deploy);
 *  - an answer that is not ours (an HTML page where the API answers JSON).
 * A 401 or 403 from our API (JSON) is never "unreachable": that is the server
 * speaking.
 */
export function isServerUnreachable(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as HttpErrorLike;
  if (e.code === 'ERR_CANCELED' || axios.isCancel(error)) return false;
  const res = e.response;
  if (!res) {
    // Only an HTTP request that got no answer; any other thrown value is a bug, not an outage.
    return e.isAxiosError === true;
  }
  if (typeof res.status === 'number' && GATEWAY_STATUSES.has(res.status)) return true;
  return isForeignResponse(res);
}

/** An error for a "successful" answer that did not come from our API (see isForeignResponse). */
export function foreignResponseError(res: HttpAnswer): Error & HttpErrorLike {
  return Object.assign(new Error('The answer did not come from LexiBridge'), {
    isAxiosError: true,
    code: 'ERR_NOT_LEXIBRIDGE',
    response: { status: res.status, headers: res.headers },
  });
}

// ---- single-flight refresh -------------------------------------------------
let refreshPromise: Promise<AxiosResponse> | null = null;

// Told after every successful refresh. Background pollers that must not refresh
// the session themselves (they would keep an idle tab signed in forever) use
// this to resume once something the user did has renewed it (ST-00215).
const refreshListeners = new Set<SessionRefreshedListener>();
export function onSessionRefreshed(listener: SessionRefreshedListener): () => boolean {
  refreshListeners.add(listener);
  return () => refreshListeners.delete(listener);
}

export function refreshSession(): Promise<AxiosResponse> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${apiBase()}/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        // A web filter's page answered in the API's place: nothing was refreshed.
        if (isForeignResponse(res)) throw foreignResponseError(res);
        refreshListeners.forEach((listener) => {
          try {
            listener();
          } catch (_) {
            /* a listener must not break the refresh */
          }
        });
        return res;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// ---- CSRF (double-submit): read the readable lb_csrf cookie ----------------
export function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)lb_csrf=([^;]*)/);
  // The capture group always takes part in a match (possibly empty).
  return m ? decodeURIComponent(m[1]!) : null;
}

// ---- "auth lost" callback (AuthContext registers a redirect to /login) -----
// Called with the refresh error, so a refused refresh (e.g. 403 tenant_suspended)
// can say why on the sign-in screen.
let onAuthLost: AuthLostHandler | null = null;
export function setOnAuthLost(fn: AuthLostHandler | null): void {
  onAuthLost = fn;
}
function authLost(error: unknown): void {
  if (onAuthLost) {
    try {
      onAuthLost(error);
    } catch (_) {
      /* noop */
    }
  }
}

// ---- "access blocked" callback (ST-00215, audit IAM-14 / IAM-11) -------------
// The server answers 403 with one of these codes when the session is valid but
// may not be used right now: the user must change their password first, or their
// organisation is suspended / expired. AuthContext registers what to do (go to
// the change-password screen, or back to sign-in with the message).
export const ACCESS_BLOCKED_CODES: readonly AccessBlockedCode[] = [
  'password_change_required',
  'tenant_suspended',
  'tenant_expired',
];
let onAccessBlocked: AccessBlockedHandler | null = null;
export function setOnAccessBlocked(fn: AccessBlockedHandler | null): void {
  onAccessBlocked = fn;
}
function isAccessBlockedCode(code: unknown): code is AccessBlockedCode {
  return (ACCESS_BLOCKED_CODES as readonly unknown[]).includes(code);
}
/** The blocking code of an axios error (or null). */
export function accessBlockedCode(error: unknown): AccessBlockedCode | null {
  const res = (error as HttpErrorLike | null | undefined)?.response;
  const code = res && res.status === 403 && res.data && res.data.code;
  return isAccessBlockedCode(code) ? code : null;
}
function accessBlocked(code: AccessBlockedCode, error: unknown): void {
  if (onAccessBlocked) {
    try {
      onAccessBlocked(code, (error as HttpErrorLike | undefined)?.response?.data?.message || null);
    } catch (_) {
      /* noop */
    }
  }
}

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);
function isAuthBootstrap(url: string | undefined = ''): boolean {
  return /\/auth\/(refresh|login|logout|me|forgot-password|set-password)/.test(url);
}

/** A request config that remembers it was already retried after a refresh. */
type RetriableRequestConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

/**
 * Install withCredentials + CSRF + single-flight-refresh interceptors on an
 * axios instance. Returns the instance.
 */
export function installAuthInterceptors(instance: AxiosInstance): AxiosInstance {
  instance.defaults.withCredentials = true;

  instance.interceptors.request.use((config) => {
    const method = (config.method || 'get').toLowerCase();
    if (MUTATING.has(method)) {
      const t = getCsrfToken();
      // A plain object, as before the conversion: axios turns it back into
      // AxiosHeaders when it dispatches the request.
      if (t)
        config.headers = {
          ...(config.headers || {}),
          'X-CSRF-Token': t,
        } as unknown as AxiosRequestHeaders;
    }
    return config;
  });

  instance.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const original = error.config as RetriableRequestConfig | undefined;
      const status = error.response && error.response.status;
      if (status === 401 && original && !original._authRetried && !isAuthBootstrap(original.url)) {
        original._authRetried = true;
        try {
          await refreshSession(); // shared single-flight across all clients
          return instance(original); // retry once with the fresh cookie
        } catch (refreshError) {
          // LexiBridge could not be reached (network, web filter, deploy): the
          // session may be perfectly good, so do not sign out. The request fails
          // like any request that cannot get through (ST-00303).
          if (isServerUnreachable(refreshError)) return Promise.reject(refreshError);
          authLost(refreshError); // the server refused the refresh -> session is gone
          return Promise.reject(error);
        }
      }
      const blocked = accessBlockedCode(error);
      if (blocked && !isAuthBootstrap(original && original.url)) accessBlocked(blocked, error);
      return Promise.reject(error);
    }
  );

  return instance;
}
